import { ChevronDown, ChevronUp, ZoomIn } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { GALLERY_IMAGES, type GalleryImage, type GallerySize } from "../../data/content";
import { cn, unsplash } from "../../utils/format";
import { Button } from "../ui/Button";
import { Modal, ModalCloseButton } from "../ui/Modal";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";

const INITIAL_VISIBLE = 6;

const SIZE_CLASSES: Record<GallerySize, string> = {
  grande: "col-span-2 row-span-2",
  vertical: "row-span-2",
  horizontal: "col-span-2",
  normal: "",
};

export const Galeria = () => {
  const [visivel, setVisivel] = useState(INITIAL_VISIBLE);
  const [imagemAtiva, setImagemAtiva] = useState<GalleryImage | null>(null);
  // Mantém a última imagem durante a animação de saída do lightbox.
  const [lastImage, setLastImage] = useState<GalleryImage | null>(null);
  if (imagemAtiva && imagemAtiva !== lastImage) setLastImage(imagemAtiva);

  const showingAll = visivel >= GALLERY_IMAGES.length;
  const toggleGaleria = () => setVisivel(showingAll ? INITIAL_VISIBLE : GALLERY_IMAGES.length);

  return (
    <section id="galeria" aria-labelledby="galeria-title" className="bg-cream py-24 text-night lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading
            id="galeria-title"
            tone="cream"
            eyebrow="Galeria"
            title="Nossa"
            highlight="Galeria"
            description="Veja alguns dos nossos burgers mais pedidos"
          />
          <Button
            variant="dark"
            onClick={toggleGaleria}
            aria-expanded={showingAll}
            icon={
              showingAll ? (
                <ChevronUp className="size-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="size-4" aria-hidden="true" />
              )
            }
          >
            {showingAll ? "Mostrar Menos" : "Mostrar Mais"}
          </Button>
        </Reveal>

        <Reveal className="mt-12">
          <motion.ul
            layout
            className="grid grid-flow-dense auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[200px] md:grid-cols-4 md:gap-4"
          >
            <AnimatePresence initial={false}>
              {GALLERY_IMAGES.slice(0, visivel).map((img, index) => (
                <motion.li
                  layout
                  key={img.url}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index >= INITIAL_VISIBLE ? (index - INITIAL_VISIBLE) * 0.05 : 0 }}
                  className={cn("group relative overflow-hidden rounded-2xl bg-cream-dark", SIZE_CLASSES[img.size])}
                >
                  <button
                    type="button"
                    onClick={() => setImagemAtiva(img)}
                    aria-label={`Ampliar foto ${index + 1} da galeria`}
                    className="block size-full focus-visible:outline-night"
                  >
                    <img
                      src={unsplash(img.url, img.size === "grande" ? 1000 : 600)}
                      alt={`Prato do Ninja Burger, foto ${index + 1}`}
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                    <span className="absolute inset-0 grid place-items-center bg-night/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <span className="grid size-12 place-items-center rounded-full bg-primary text-night">
                        <ZoomIn className="size-5" aria-hidden="true" />
                      </span>
                    </span>
                  </button>
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        </Reveal>
      </div>

      <Modal open={imagemAtiva !== null} onClose={() => setImagemAtiva(null)} variant="media" label="Foto ampliada">
        {lastImage && (
          <div className="relative">
            <img
              src={unsplash(lastImage.url, 1600)}
              alt="Foto ampliada da galeria Ninja Burger"
              className="max-h-[86dvh] w-full rounded-2xl object-contain"
            />
            <ModalCloseButton onClick={() => setImagemAtiva(null)} className="absolute right-3 top-3" />
          </div>
        )}
      </Modal>
    </section>
  );
};
